import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Bell, Info, XCircle, AlertCircle, ArrowRight } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

/* Severity → icon/color mapping (also used by the Notifications page) */
export const getSeverityConfig = (severity) => {
  switch (severity) {
    case "action":
      return {
        icon: <ArrowRight className="h-4 w-4" />,
        color: "text-indigo-600 bg-indigo-50 dark:bg-indigo-500/10",
      };
    case "warning":
      return {
        icon: <AlertCircle className="h-4 w-4" />,
        color: "text-amber-600 bg-amber-50 dark:bg-amber-500/10",
      };
    case "critical":
      return {
        icon: <XCircle className="h-4 w-4" />,
        color: "text-red-600 bg-red-50 dark:bg-red-500/10",
      };
    default: // info
      return {
        icon: <Info className="h-4 w-4" />,
        color: "text-sky-600 bg-sky-50 dark:text-sky-400 dark:bg-sky-500/10",
      };
  }
};

/**
 * Reusable notifications bell + dropdown panel.
 *
 * UI is shared; data and behavior are supplied by the parent so each screen
 * (employee, admin, …) keeps its own data source and click-routing.
 *
 * Props:
 *  - notifications: array
 *  - unreadCount: number
 *  - onNotificationClick(notification): called when a row is clicked
 *  - onMarkAll(): mark all as read
 *  - onClearAll(): clear all
 *  - onViewAll(): open the full Notifications page (optional)
 *  - loading: boolean (optional) — shows a spinner in the body
 *  - triggerClassName: string (optional) — sizes the bell button
 *  - align: "start" | "center" | "end" (optional) — dropdown alignment
 */
const NotificationsMenu = ({
  notifications = [],
  unreadCount = 0,
  onNotificationClick,
  onMarkAll,
  onClearAll,
  onViewAll,
  loading = false,
  triggerClassName = "relative h-9 w-9",
  align = "end",
}) => {
  // Each instance owns its open state so multiple mounted instances
  // (e.g. responsive headers) don't pop open together.
  const [open, setOpen] = useState(false);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={triggerClassName}
          data-testid="notifications-btn"
          aria-label="Notifications"
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] sm:min-w-[18px] h-4 sm:h-[18px] px-1 rounded-full bg-red-600 text-white text-[9px] sm:text-[10px] font-bold flex items-center justify-center shadow-sm">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align={align}
        sideOffset={8}
        collisionPadding={12}
        className="w-[calc(100vw-2rem)] max-w-[22rem] sm:w-[360px] p-0 overflow-hidden"
      >
        {/* Header */}
        <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-border flex items-center justify-between bg-slate-50/70 dark:bg-slate-900/60">
          <p className="text-xs sm:text-sm font-extrabold tracking-tight text-slate-900 dark:text-white">
            Notifications
          </p>
          {unreadCount > 0 && (
            <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-[#3194A0]">
              {unreadCount} unread
            </span>
          )}
        </div>

        {/* Body */}
        <div className="max-h-[50vh] sm:max-h-[300px] overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800/50 custom-scrollbar">
          {loading ? (
            <div className="flex items-center justify-center py-10 gap-3 text-sm text-slate-400">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-transparent" />
              Loading notifications…
            </div>
          ) : notifications.length === 0 ? (
            <div className="py-12 sm:py-14 text-center flex flex-col items-center gap-3 sm:gap-4 px-4 sm:px-6">
              <div className="h-12 w-12 sm:h-14 sm:w-14 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                <Bell className="h-6 w-6 sm:h-7 sm:w-7 text-slate-400 dark:text-slate-500" />
              </div>
              <p className="text-xs sm:text-sm font-semibold text-slate-500">
                No notifications yet
              </p>
            </div>
          ) : (
            notifications.map((notification) => {
              const { icon, color } = getSeverityConfig(notification.type);
              const message = notification.message || "";
              const truncatedMessage =
                message.length > 120 ? message.slice(0, 120) + "…" : message;
              const createdDate = notification.createdAt
                ? new Date(notification.createdAt)
                : null;
              const createdLabel =
                createdDate && !Number.isNaN(createdDate.getTime())
                  ? formatDistanceToNow(createdDate, { addSuffix: true })
                  : "";

              return (
                <div
                  key={notification.id ?? notification._id}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    setOpen(false);
                    onNotificationClick?.(notification);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setOpen(false);
                      onNotificationClick?.(notification);
                    }
                  }}
                  className={`group relative px-4 sm:px-5 py-3 sm:py-4 cursor-pointer transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40
                ${
                  !notification.is_read
                    ? "bg-primary/5 hover:bg-primary/10"
                    : "hover:bg-slate-50 dark:hover:bg-slate-800/40"
                }
              `}
                >
                  {!notification.is_read && (
                    <span className="absolute left-0 top-0 bottom-0 w-[2px] sm:w-[3px] bg-primary rounded-r-full" />
                  )}

                  <div className="flex gap-3 sm:gap-4">
                    <div
                      className={`mt-0.5 h-8 w-8 sm:h-9 sm:w-9 shrink-0 rounded-xl flex items-center justify-center ${color} transition-transform duration-200 group-hover:scale-105`}
                    >
                      {icon}
                    </div>

                    <div className="flex-1 min-w-0 space-y-1 sm:space-y-1.5">
                      <div className="flex items-start justify-between gap-2">
                        <p
                          className={`text-xs sm:text-sm leading-snug truncate ${
                            !notification.is_read
                              ? "font-bold text-slate-900 dark:text-white"
                              : "font-semibold text-slate-600 dark:text-slate-400"
                          }`}
                        >
                          {notification.title}
                        </p>

                        {createdLabel && (
                          <span className="shrink-0 text-[8px] sm:text-[9px] font-black uppercase tracking-tight text-slate-400">
                            {createdLabel}
                          </span>
                        )}
                      </div>

                      <p
                        className={`text-[11px] sm:text-xs leading-relaxed line-clamp-2 ${
                          !notification.is_read
                            ? "text-slate-600 dark:text-slate-300"
                            : "text-slate-400 dark:text-slate-500"
                        }`}
                      >
                        {truncatedMessage}
                      </p>

                      <div className="flex items-center justify-between pt-0.5 sm:pt-1">
                        <span className="text-[9px] sm:text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">
                          {notification.severity}
                        </span>

                        {!notification.is_read && (
                          <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer (one row) */}
        {notifications.length > 0 && (
          <div className="flex items-stretch border-t border-slate-200/60 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/50">
            <button
              onClick={() => {
                onMarkAll?.();
                setOpen(false);
              }}
              disabled={unreadCount === 0}
              className="flex-1 px-3 sm:px-4 py-2.5 sm:py-3 text-[10px] sm:text-xs font-extrabold text-[#3194A0]
          hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40 transition-colors uppercase tracking-widest"
            >
              Mark all read
            </button>

            <button
              onClick={() => {
                onClearAll?.();
                setOpen(false);
              }}
              className="flex-1 px-3 sm:px-4 py-2.5 sm:py-3 text-[10px] sm:text-xs font-extrabold text-rose-500
          hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors uppercase tracking-widest border-l border-slate-200/60 dark:border-slate-800"
            >
              Clear all
            </button>
          </div>
        )}

        {onViewAll && (
          <button
            onClick={() => {
              setOpen(false);
              onViewAll();
            }}
            className="w-full border-t border-slate-200/60 dark:border-slate-800 px-4 py-2.5 sm:py-3 text-[10px] sm:text-xs font-extrabold uppercase tracking-widest text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            View all notifications
          </button>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationsMenu;
